import { ChevronLeft, ChevronRight, Edit, Plus, Search, Trash2, X } from 'lucide-react'
import { Fragment, useCallback, useEffect, useRef, useState } from 'react'
import Layout from '../../components/Layout'
import { Button } from '../../components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card'
import { Input } from '../../components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select'
import { SortableTableHeader, useSortableTable } from '../../components/ui/sortable-table'
import { useNotification } from '../../contexts/NotificationContext'
import {
  deletePlayer,
  fetchPlayers as fetchPlayersApi,
  insertPlayer,
  updatePlayer,
} from '../../services/playerService'
import { fetchActiveSports } from '../../services/leaderboardService'

export default function PlayerManagement({ embedded = false }) {
  const Wrapper = embedded ? Fragment : Layout
  const notify = useNotification()
  const [searchTerm, setSearchTerm] = useState('')
  const [filterGender, setFilterGender] = useState('all')
  const [filterSport, setFilterSport] = useState('all')
  const [currentPage, setCurrentPage] = useState(1)
  const [rowsPerPage, setRowsPerPage] = useState(10)
  const [showEditModal, setShowEditModal] = useState(false)
  const [showAddModal, setShowAddModal] = useState(false)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [editingPlayer, setEditingPlayer] = useState(null)
  const [deletingPlayer, setDeletingPlayer] = useState(null)
  const [players, setPlayers] = useState([])
  const [totalCount, setTotalCount] = useState(0)
  const [loading, setLoading] = useState(true)
  const [sports, setSports] = useState([])
  const [editForm, setEditForm] = useState({
    name: '',
    gender: '',
    sport_id: '',
  })
  const [addForm, setAddForm] = useState({
    name: '',
    gender: '',
    sport_id: '',
  })
  
  // Debounce timer ref
  const searchTimeoutRef = useRef(null)
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('')

  // Debounce search term
  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current)
    }
    searchTimeoutRef.current = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm)
      setCurrentPage(1)
    }, 300)
    
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current)
      }
    }
  }, [searchTerm])

  // Fetch players from database
  const fetchPlayers = useCallback(async () => {
    setLoading(true)
    try {
      const offset = (currentPage - 1) * rowsPerPage
      const data = await fetchPlayersApi({
        p_search_term: debouncedSearchTerm || null,
        p_gender: filterGender === 'all' ? null : filterGender,
        p_sport_id: filterSport === 'all' ? null : filterSport,
        p_limit: rowsPerPage,
        p_offset: offset
      })

      setPlayers(data.players || [])
      setTotalCount(data.total_count || 0)
    } catch (err) {
      console.error('Error fetching players:', err)
      notify.error('Failed to load players')
    } finally {
      setLoading(false)
    }
  }, [debouncedSearchTerm, filterGender, filterSport, currentPage, rowsPerPage])

  useEffect(() => {
    fetchPlayers()
  }, [fetchPlayers])

  useEffect(() => {
    fetchActiveSports()
      .then((data) => {
        setSports(data || [])
        // Default new-player form to Pickleball so an admin adding a player
        // doesn't have to remember to pick the sport. Owners asked for this
        // explicitly in DUB-10/17. Non-pickleball players can still be
        // switched via the dropdown.
        const pickleball = (data || []).find(s => s.name === 'Pickleball')
        if (pickleball) {
          setAddForm(prev => (prev.sport_id ? prev : { ...prev, sport_id: pickleball.id }))
        }
      })
      .catch((err) => {
        console.error('Error fetching sports:', err)
      })
  }, [])

  // Sorting (client-side for current page)
  const { sortedData, sortConfig, handleSort, SortIcon } = useSortableTable(players, 'name', 'asc')

  // Pagination calculations
  const totalPages = Math.ceil(totalCount / rowsPerPage)
  const startIndex = (currentPage - 1) * rowsPerPage
  const endIndex = Math.min(startIndex + rowsPerPage, totalCount)

  const handleRowsPerPageChange = (value) => {
    setRowsPerPage(value)
    setCurrentPage(1)
  }

  const handleEditClick = (player) => {
    setEditingPlayer(player)
    setEditForm({
      name: player.name,
      // Capitalize first letter for display in select
      gender: player.gender.charAt(0).toUpperCase() + player.gender.slice(1),
      sport_id: player.sport_id || '',
    })
    setShowEditModal(true)
  }

  const handleSaveEdit = async () => {
    if (!editForm.name || !editForm.gender) {
      notify.error('Please fill in all required fields')
      return
    }

    try {
      await updatePlayer(editingPlayer.id, {
        name: editForm.name.toUpperCase(),
        gender: editForm.gender.toLowerCase(),
        sport_id: editForm.sport_id || null,
      })
      
      notify.success(`Player "${editForm.name}" updated successfully!`)
      setShowEditModal(false)
      setEditingPlayer(null)
      fetchPlayers()
    } catch (err) {
      console.error('Error updating player:', err)
      notify.error('Failed to update player')
    }
  }

  const handleDeleteClick = (player) => {
    setDeletingPlayer(player)
    setShowDeleteModal(true)
  }

  const handleConfirmDelete = async () => {
    try {
      await deletePlayer(deletingPlayer.id)
      
      notify.success(`Player "${deletingPlayer.name}" deleted successfully!`)
      setShowDeleteModal(false)
      setDeletingPlayer(null)
      fetchPlayers()
    } catch (err) {
      console.error('Error deleting player:', err)
      notify.error('Failed to delete player. Player may be in use.')
    }
  }

  const handleCancelDelete = () => {
    setShowDeleteModal(false)
    setDeletingPlayer(null)
  }

  const handleCancelEdit = () => {
    setShowEditModal(false)
    setEditingPlayer(null)
    setEditForm({
      name: '',
      gender: '',
      sport_id: '',
    })
  }

  // Pickleball is the default sport for new players — owners asked for this
  // in DUB-10/17 so admins don't have to set it every time.
  const emptyAddForm = () => ({
    name: '',
    gender: '',
    sport_id: sports.find(s => s.name === 'Pickleball')?.id || '',
  })

  const handleAddClick = () => {
    setAddForm(emptyAddForm())
    setShowAddModal(true)
  }

  const handleSaveAdd = async () => {
    if (!addForm.name || !addForm.gender) {
      notify.error('Please fill in all required fields')
      return
    }

    try {
      await insertPlayer({
        name: addForm.name.toUpperCase(),
        gender: addForm.gender.toLowerCase(),
        sport_id: addForm.sport_id || null,
      })

      notify.success(`Player "${addForm.name}" added successfully!`)
      setShowAddModal(false)
      setAddForm(emptyAddForm())
      fetchPlayers()
    } catch (err) {
      console.error('Error adding player:', err)
      notify.error('Failed to add player')
    }
  }

  const handleCancelAdd = () => {
    setShowAddModal(false)
    setAddForm(emptyAddForm())
  }

  return (
    <Wrapper>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold mb-2">Player Management</h1>
            <p className="text-muted-foreground">Manage the player database</p>
          </div>
          <Button onClick={handleAddClick} className="w-full sm:w-auto">
            <Plus className="h-4 w-4 mr-2" />
            Add Player
          </Button>
        </div>

        {/* Filters */}
        <Card className="mb-6">
          <CardContent className="pt-6">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              {/* Search */}
              <div className="md:col-span-2">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <input
                    type="text"
                    placeholder="Search players..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-10 py-2 border rounded-md bg-background"
                  />
                  {searchTerm && (
                    <button
                      type="button"
                      onClick={() => setSearchTerm('')}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  )}
                </div>
              </div>

              {/* Gender Filter */}
              <div>
                <Select value={filterGender} onValueChange={(value) => {
                  setFilterGender(value)
                  setCurrentPage(1)
                }}>
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Both Genders" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Both Genders</SelectItem>
                    <SelectItem value="male">Male</SelectItem>
                    <SelectItem value="female">Female</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Sport Filter */}
              <div>
                <Select value={filterSport} onValueChange={(value) => {
                  setFilterSport(value)
                  setCurrentPage(1)
                }}>
                  <SelectTrigger className="w-full" aria-label="Filter by sport">
                    <SelectValue placeholder="All Sports" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Sports</SelectItem>
                    {sports.map((s) => (
                      <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Player Table */}
        <Card>
          <CardHeader>
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <CardTitle className="flex items-center gap-2">Players <span className="text-muted-foreground font-normal">{totalCount}</span></CardTitle>
              <div className="flex flex-col items-start gap-2 sm:flex-row sm:items-center">
                <span className="text-sm text-muted-foreground">Rows per page:</span>
                <Select value={rowsPerPage.toString()} onValueChange={(value) => handleRowsPerPageChange(parseInt(value))}>
                  <SelectTrigger className="w-[80px] text-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="10">10</SelectItem>
                    <SelectItem value="25">25</SelectItem>
                    <SelectItem value="50">50</SelectItem>
                    <SelectItem value="100">100</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full min-w-[520px]">
                <thead>
                  <tr className="border-b">
                    <SortableTableHeader
                      onClick={() => handleSort('name')}
                      className="text-left py-3 px-4"
                    >
                      Name <SortIcon column="name" />
                    </SortableTableHeader>
                    <SortableTableHeader
                      onClick={() => handleSort('gender')}
                      className="text-left py-3 px-4"
                    >
                      Gender <SortIcon column="gender" />
                    </SortableTableHeader>
                    <SortableTableHeader
                      onClick={() => handleSort('sport_name')}
                      className="text-left py-3 px-4"
                    >
                      Sport <SortIcon column="sport_name" />
                    </SortableTableHeader>
                    <th className="text-right py-3 px-4">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan={4} className="py-8 text-center text-muted-foreground">
                        Loading players...
                      </td>
                    </tr>
                  ) : sortedData.length === 0 ? (
                    <tr>
                      <td colSpan={4} className="py-8 text-center text-muted-foreground">
                        No players found
                      </td>
                    </tr>
                  ) : (
                    sortedData.map((player) => (
                      <tr key={player.id} className="border-b hover:bg-muted/50">
                        <td className="py-3 px-4 font-medium">{player.name}</td>
                        <td className="py-3 px-4 capitalize">{player.gender}</td>
                        <td className="py-3 px-4 text-muted-foreground">{player.sport_name || '—'}</td>
                        <td className="py-3 px-4">
                          <div className="flex items-center justify-end gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleEditClick(player)}
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeleteClick(player)}
                            >
                              <Trash2 className="h-4 w-4 text-destructive" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
            
            {/* Pagination Controls */}
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mt-4 pt-4 border-t">
              <div className="text-sm text-muted-foreground">
                {totalCount > 0 
                  ? `Showing ${startIndex + 1} to ${endIndex} of ${totalCount} players`
                  : 'No players found'}
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                  disabled={currentPage === 1}
                >
                  <ChevronLeft className="h-4 w-4" />
                  Previous
                </Button>
                <div className="flex flex-wrap items-center gap-1">
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    let pageNum
                    if (totalPages <= 5) {
                      pageNum = i + 1
                    } else if (currentPage <= 3) {
                      pageNum = i + 1
                    } else if (currentPage >= totalPages - 2) {
                      pageNum = totalPages - 4 + i
                    } else {
                      pageNum = currentPage - 2 + i
                    }
                    return (
                      <Button
                        key={pageNum}
                        variant={currentPage === pageNum ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => setCurrentPage(pageNum)}
                        className="w-10"
                      >
                        {pageNum}
                      </Button>
                    )
                  })}
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                  disabled={currentPage === totalPages}
                >
                  Next
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Add Player Modal */}
        {showAddModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
            <Card className="w-full max-w-md max-h-[90vh] overflow-y-auto">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle>Add New Player</CardTitle>
                  <Button variant="ghost" size="icon" onClick={handleCancelAdd}>
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div>
                    <label className="text-sm font-medium mb-2 block">Player Name *</label>
                    <Input
                      value={addForm.name}
                      onChange={(e) => setAddForm({ ...addForm, name: e.target.value })}
                      placeholder="Enter player name"
                    />
                  </div>

                  <div>
                    <label className="text-sm font-medium mb-2 block">Gender *</label>
                    <Select value={addForm.gender || undefined} onValueChange={(value) => setAddForm({ ...addForm, gender: value })}>
                      <SelectTrigger className="w-full">
                        <SelectValue placeholder="Select gender" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Male">Male</SelectItem>
                        <SelectItem value="Female">Female</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <label className="text-sm font-medium mb-2 block">Sport</label>
                    <Select value={addForm.sport_id || undefined} onValueChange={(value) => setAddForm({ ...addForm, sport_id: value })}>
                      <SelectTrigger className="w-full">
                        <SelectValue placeholder="Select sport (optional)" />
                      </SelectTrigger>
                      <SelectContent>
                        {sports.map((s) => (
                          <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {(!addForm.name || !addForm.gender) && (
                    <div className="p-2 bg-[var(--orange-3)] border border-[var(--orange-6)] rounded-lg">
                      <p className="text-xs text-[var(--orange-12)]">
                        Please enter player name and select gender
                      </p>
                    </div>
                  )}
                </div>

                <div className="flex gap-2 pt-4 mt-4 border-t">
                  <Button variant="outline" onClick={handleCancelAdd} className="flex-1">
                    Cancel
                  </Button>
                  <Button 
                    onClick={handleSaveAdd} 
                    className="flex-1"
                    disabled={!addForm.name || !addForm.gender}
                  >
                    Add Player
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Edit Player Modal */}
        {showEditModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
            <Card className="w-full max-w-md max-h-[90vh] overflow-y-auto">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle>Edit Player</CardTitle>
                  <Button variant="ghost" size="icon" onClick={handleCancelEdit}>
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div>
                    <label className="text-sm font-medium mb-2 block">Player Name *</label>
                    <Input
                      value={editForm.name}
                      onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                      placeholder="Enter player name"
                    />
                  </div>

                  <div>
                    <label className="text-sm font-medium mb-2 block">Gender *</label>
                    <Select value={editForm.gender || undefined} onValueChange={(value) => setEditForm({ ...editForm, gender: value })}>
                      <SelectTrigger className="w-full">
                        <SelectValue placeholder="Select gender" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Male">Male</SelectItem>
                        <SelectItem value="Female">Female</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <label className="text-sm font-medium mb-2 block">Sport</label>
                    <Select value={editForm.sport_id || undefined} onValueChange={(value) => setEditForm({ ...editForm, sport_id: value })}>
                      <SelectTrigger className="w-full">
                        <SelectValue placeholder="Select sport (optional)" />
                      </SelectTrigger>
                      <SelectContent>
                        {sports.map((s) => (
                          <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {(!editForm.name || !editForm.gender) && (
                    <div className="p-2 bg-[var(--orange-3)] border border-[var(--orange-6)] rounded-lg">
                      <p className="text-xs text-[var(--orange-12)]">
                        Please enter player name and select gender
                      </p>
                    </div>
                  )}
                </div>

                <div className="flex gap-2 pt-4 mt-4 border-t">
                  <Button
                    variant="outline"
                    onClick={handleCancelEdit}
                    className="flex-1"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleSaveEdit}
                    className="flex-1"
                    disabled={!editForm.name || !editForm.gender}
                  >
                    Save Changes
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Delete Confirmation Modal */}
        {showDeleteModal && deletingPlayer && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
            <Card className="w-full max-w-md">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle>Delete Player</CardTitle>
                  <Button variant="ghost" size="icon" onClick={handleCancelDelete}>
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground mb-6">
                  Are you sure you want to delete <span className="font-semibold text-foreground">{deletingPlayer.name}</span>? 
                  This action cannot be undone.
                </p>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    onClick={handleCancelDelete}
                    className="flex-1"
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="destructive"
                    onClick={handleConfirmDelete}
                    className="flex-1"
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    Delete Player
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </Wrapper>
  )
}
